import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, Lock, Power } from "lucide-react";

// Owner activation / deactivation of the included (Core Standard) Customer Credit module. Reuses the backend credit engine's
// existing modes: Open Credit == Open Account (`open`), Credit Line == Credit Limit (`limit`). No billing involvement.
const MODELS = [
  { v: "open", label: "Open Credit", desc: "Approved customers run a tab with no fixed ceiling. Approvals, balances, payments, aging and audit history apply as usual." },
  { v: "limit", label: "Credit Line", desc: "Each customer gets a defined credit limit. Available credit and limit controls are enforced at checkout." },
];

export function useCreditModuleStatus() {
  const [status, setStatus] = useState(null);
  const load = useCallback(() => api.get("/business/modules/customers_credit").then((r) => setStatus(r.data)).catch(() => setStatus(null)), []);
  useEffect(() => { load(); }, [load]);
  return [status, load];
}

export function CreditActivationCard() {
  const { user, loadBusiness } = useAuth();
  const [status, reload] = useCreditModuleStatus();
  const [mode, setMode] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const owner = user?.role === "owner";

  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/business/modules/customers_credit/activate", { credit_mode: mode, confirm });
      toast.success(`Customer Credit activated — ${MODELS.find((m) => m.v === mode)?.label}`);
      await loadBusiness(); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (status?.locked_by_neec) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm" data-testid="credit-locked-by-neec">
        <p className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-amber-500" /> Customer Credit is turned off for this business</p>
        <p className="text-muted-foreground mt-1">Customer Credit is included in your Standard plan, but it was turned off by NEEC POS. Contact NEEC POS support to restore it.{status.has_history ? " Existing balances remain available for collection from the Customers page." : ""}</p>
      </div>
    );
  }
  if (!owner) {
    return (
      <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border p-3" data-testid="settings-credit-module-off">
        Customer Credit is included in your Standard plan but has not been activated yet. Only the Business Owner can activate it from this page.
        {status?.has_history ? " Existing balances remain available for collection from the Customers page." : ""}
      </p>
    );
  }
  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 space-y-4" data-testid="credit-activation-card">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0"><CreditCard className="w-6 h-6" /></div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-head font-bold text-lg" data-testid="credit-activation-heading">Customer Credit — Included in Your Standard Plan</p>
            <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Core Standard</Badge>
            <Badge variant="outline" className="text-[10px]" data-testid="credit-activation-status">Not Activated</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Customer Credit is included with NEEC P-O-S at no additional module charge. Activate it when your business is ready to offer customers credit accounts. Nothing changes on your subscription or billing.</p>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">1 · Choose your credit model</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {MODELS.map((m) => (
            <button key={m.v} type="button" onClick={() => setMode(m.v)} data-testid={`credit-model-${m.v}`} aria-pressed={mode === m.v}
              className={`text-left rounded-xl border p-3.5 transition-colors ${mode === m.v ? "border-emerald-500 bg-emerald-500/10" : "border-border hover:border-emerald-500/40"}`}>
              <p className="font-medium text-sm">{m.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{m.desc}</p>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">This sets the default for new credit customers; you can still choose per customer. Existing customers are never changed.</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">2 · Confirm</p>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} data-testid="credit-activation-confirm" className="mt-0.5" />
          <span>I understand that activating Customer Credit allows authorised staff to sell on credit to approved customers under the existing approval, limit and audit rules. Only I (the Business Owner) can activate or deactivate it.</span>
        </label>
      </div>
      <Button onClick={activate} disabled={!mode || !confirm || busy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6" data-testid="credit-activate-btn">
        <ShieldCheck className="w-4 h-4 mr-1.5" /> {busy ? "Activating…" : "Activate Customer Credit"}
      </Button>
    </div>
  );
}

export function CreditDeactivationBox() {
  const { loadBusiness } = useAuth();
  const [status, reload] = useCreditModuleStatus();
  const [busy, setBusy] = useState(false);
  const impact = status?.impact;

  const deactivate = async () => {
    setBusy(true);
    try {
      await api.post("/business/modules/customers_credit/deactivate", { confirm: true });
      toast.success("Customer Credit deactivated — existing balances and history preserved");
      await loadBusiness(); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border p-4 mt-3" data-testid="credit-deactivation-box">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-medium text-sm flex items-center gap-2"><Power className="w-4 h-4 text-muted-foreground" /> Customer Credit is <span className="text-emerald-600" data-testid="credit-active-status">Active</span> · Core Standard, included in your plan</p>
          <p className="text-xs text-muted-foreground mt-1">Deactivating stops <b>new</b> credit sales and new credit setup. Existing balances, invoices, payments, aging, statements and collections stay exactly as they are — nothing is deleted, reset or written off.</p>
          {status?.activated_at && <p className="text-[11px] text-muted-foreground mt-1">Activated {String(status.activated_at).slice(0, 10)}{status.updated_by ? ` · by ${status.updated_by}` : ""}</p>}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-red-600 border-red-500/30 hover:bg-red-500/5" data-testid="credit-deactivate-btn">Deactivate Customer Credit</Button></AlertDialogTrigger>
          <AlertDialogContent data-testid="credit-deactivate-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate Customer Credit?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  {impact && impact.customers > 0
                    ? <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-amber-700" data-testid="credit-deactivate-impact">{impact.message}</p>
                    : <p data-testid="credit-deactivate-impact">No customer currently owes a credit balance.</p>}
                  <p>After deactivation: no new credit sales or credit-limit increases. Existing balances, invoices, payments, statements and aging remain available and collectible. You can reactivate at any time from this page.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="credit-deactivate-cancel">Keep Active</AlertDialogCancel>
              <AlertDialogAction onClick={deactivate} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="credit-deactivate-confirm">Deactivate</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
